import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageShell, ShellSection } from "./PageShell";
import { METHOD, PRINCIPLES, LEADERSHIP } from "../data/content";
import { Reveal } from "../components/Reveal";

export default function CompanyPage() {
    return (
        <PageShell
            testId="company-page"
            overline="Company"
            metaTitle="Company — Titan Haven Group LLC"
            title="Titan Haven Group is a builder and operator."
            lede="Titan Haven Group LLC — a Wyoming LLC with East Coast operations — builds and operates software, emerging technology, consumer businesses, digital assets, and media/IP. Consulting, diagnostics, and research remain available capabilities, but they are not the center of the company. The work is."
        >
            <ShellSection>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <Reveal>
                        <h2 className="font-display text-2xl md:text-3xl font-medium text-white">Corporate identity</h2>
                        <p className="mt-4 text-sm md:text-base leading-relaxed text-thg-secondary">
                            The official Titan Haven Group LLC mark. The shield functions as the company's
                            institutional signature across products, publications, and properties.
                        </p>
                        <p className="mt-4 text-xs tracking-[0.2em] uppercase text-thg-tertiary">
                            Titan Haven Group LLC &middot; Wyoming limited liability company
                        </p>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <div className="border border-thg-border bg-[#0d0d0d] px-10 py-14 flex flex-col items-center text-center" data-testid="identity-plate">
                            <img
                                src="/assets/thg-shield.png"
                                alt="Titan Haven Group LLC shield"
                                className="w-24 md:w-28 h-auto"
                            />
                            <div className="mt-7 h-px w-16 bg-thg-red" aria-hidden="true" />
                            <p className="mt-7 font-display text-xl md:text-2xl tracking-[0.25em] text-white">
                                TITAN HAVEN GROUP
                            </p>
                            <p className="mt-2 text-xs tracking-[0.35em] text-thg-secondary">LLC</p>
                        </div>
                    </Reveal>
                </div>
            </ShellSection>
            <ShellSection>
                <Reveal>
                    <h2 className="font-display text-2xl md:text-3xl font-medium text-white">How the company works</h2>
                </Reveal>
                <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-thg-border">
                    {METHOD.map((m, i) => (
                        <Reveal key={m.step} delay={i * 0.08} className="border-b border-r border-thg-border p-8">
                            <span className="text-xs tracking-[0.2em] font-bold text-thg-red">{m.step}</span>
                            <h3 className="mt-3 font-display text-lg font-medium text-white uppercase tracking-wide">{m.name}</h3>
                            <p className="mt-3 text-sm leading-relaxed text-thg-secondary">{m.text}</p>
                        </Reveal>
                    ))}
                </div>
                <Reveal delay={0.2}>
                    <p className="mt-8 text-sm leading-relaxed text-thg-secondary max-w-3xl">
                        Operational intelligence is the discipline behind the method — developed in THG research and
                        operational briefs, and applied in the systems the company builds.{" "}
                        <Link
                            to="/warroom"
                            data-testid="company-oi-link"
                            className="text-thg-secondary hover:text-white transition-colors duration-300 underline underline-offset-4 decoration-thg-border"
                        >
                            Operational Briefs &amp; Research
                        </Link>
                    </p>
                </Reveal>
            </ShellSection>
            <ShellSection>
                <Reveal>
                    <h2 className="font-display text-2xl md:text-3xl font-medium text-white">An operator-led venture studio.</h2>
                    <p className="mt-6 text-sm md:text-base leading-relaxed text-thg-secondary max-w-3xl" data-testid="venture-studio-model">
                        Titan Haven Group identifies defined market gaps, builds and operates working products, and
                        selects the appropriate commercialization path for each. Depending on the opportunity, THG may
                        continue operating the product, license it, develop it with strategic partners, or transfer it
                        to an acquirer.
                    </p>
                </Reveal>
            </ShellSection>
            <ShellSection>
                <Reveal>
                    <h2 className="font-display text-2xl md:text-3xl font-medium text-white">Foundational principles</h2>
                </Reveal>
                <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                    {PRINCIPLES.map((p, i) => (
                        <Reveal key={p.name} delay={i * 0.08} className="border-t-2 border-thg-border pt-6">
                            <h3 className="font-display text-lg md:text-xl font-medium text-white">{p.name}</h3>
                            <p className="mt-3 text-sm leading-relaxed text-thg-secondary">{p.text}</p>
                        </Reveal>
                    ))}
                </div>
            </ShellSection>
            <ShellSection>
                <Reveal className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                    <div className="max-w-xl">
                        <p className="text-xs tracking-[0.2em] uppercase font-bold text-thg-tertiary">Leadership</p>
                        <p className="mt-3 font-display text-xl font-medium text-white" data-testid="leadership-name">
                            {LEADERSHIP.name}
                        </p>
                        <p className="mt-1 text-sm text-thg-secondary">
                            {LEADERSHIP.role}, {LEADERSHIP.org}
                        </p>
                        <p className="mt-4 text-sm leading-relaxed text-thg-secondary">
                            Peter Saad is the Managing Member of Titan Haven Group LLC and oversees the company's
                            operating businesses, product development, and strategic initiatives. His work focuses on
                            translating operating experience and identified market problems into businesses, products,
                            and systems.
                        </p>
                    </div>
                    <Link
                        to="/contact"
                        data-testid="company-contact-cta"
                        className="inline-flex items-center gap-2 border border-white/40 text-white hover:bg-white hover:text-black transition-colors duration-300 px-6 py-3 text-sm tracking-wide uppercase shrink-0"
                    >
                        Contact THG <ArrowRight size={16} />
                    </Link>
                </Reveal>
            </ShellSection>
        </PageShell>
    );
}
